import { useState } from 'react';
import { AlertCircle } from 'lucide-react';

import { productionApi } from 'api/production';
import CarouselLink from 'components/storefront/StorefrontConfigEditor/components/CarouselLink';
import ImageInput from 'components/storefront/StorefrontConfigEditor/components/ImageInput';
import ProductTable from 'components/storefront/StorefrontConfigEditor/components/ProductTable';
import SlugDisplay from 'components/storefront/StorefrontConfigEditor/components/SlugDisplay';
import { DYNAMIC_AISLE_TYPES } from 'components/storefront/StorefrontConfigEditor/utils/dynamicAisleTypes';
import useSellableProductTableHandlers from 'components/storefront/StorefrontConfigEditor/utils/useSellableProductTableHandlers';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';
import { Separator } from 'shadcn/components/ui/Separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'shadcn/components/ui/Tabs';
import { Textarea } from 'shadcn/components/ui/Textarea';
import { CONTENT_LINK_URL_PATTERN, CONTENT_LINK_URL_TITLE } from 'shared/utils/contentLinkUrl';

const DISPLAY_NAME = 'Product Carousel 3';

/**
 * Default configuration for the ProductCarousel3 slice component
 *
 * @constant {Object}
 * @property {string} imageUrl - URL for the stored image
 * @property {string} name - Carousel name
 * @property {string} description - Carousel description
 * @property {string} linkUrl - URL for the carousel link
 * @property {string} linkText - Text for the carousel link
 * @property {Array} items - Array of product items
 * @property {string} dynamicAisleType - Dynamic aisle type
 * @property {boolean} enablePersonalizedOrdering - Whether to enable personalized product ordering
 */
const DEFAULT_SLICE = Object.freeze({
    eyebrow: '',
    imageUrl: '',
    name: '',
    description: '',
    linkUrl: '',
    linkText: '',
    items: [],
    dynamicAisleType: '',
    enablePersonalizedOrdering: true,
});

const MIN_PRODUCT_ITEMS = 1;

/**
 * Product Carousel 3 slice component
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {Function} props.onChange - Callback function for handling slice changes
 * @param {Object} props.slice - Slice configuration object
 * @returns {JSX.Element} ProductCarousel3 component
 */
function ProductCarousel3({ disabled, index, onChange, slice }) {
    const [imageError, setImageError] = useState(null);

    const handleChange = (e) => {
        onChange({ [e.target.name]: e.target.value });
    };

    const {
        allSellableProducts,
        handleAddRow,
        handleMoveRowToIndex,
        handleRemoveAllRows,
        handleRemoveRow,
        handleRemoveSelectedRows,
        handleUploadProductCSV,
        loading,
        moveRowDown,
        moveRowUp,
        options,
    } = useSellableProductTableHandlers(slice, onChange, index);

    const enrichedRows = slice.items.map((item, index) => ({
        ...item,
        rank: index + 1,
        valid: allSellableProducts.some((product) => product.id === item.id),
    }));

    const handleImageUpload = async (file) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file);
            const imageUrl = response.data.imagePath;
            onChange({ imageUrl });
            setImageError(null);
        } catch (e) {
            setImageError(`Error: ${e.message}`);
        }
    };

    const handleClearImage = () => {
        onChange({ imageUrl: '' });
    };

    return (
        <div className="flex flex-col gap-6">
            {!slice.dynamicAisleType && slice.items.length < MIN_PRODUCT_ITEMS && (
                <Alert variant="destructive">
                    <AlertCircle />
                    <AlertTitle>Warning</AlertTitle>
                    <AlertDescription>
                        A minimum of {MIN_PRODUCT_ITEMS} sellable item or bundle is required for publication. Currently:{' '}
                        {slice.items.length}/{MIN_PRODUCT_ITEMS}
                    </AlertDescription>
                </Alert>
            )}

            <div className="flex flex-col gap-6">
                {slice.carouselID && <CarouselLink carouselID={slice.carouselID} />}

                <ImageInput
                    id={`image-${slice.id}`}
                    imageUrl={slice.imageUrl}
                    error={imageError}
                    onClearImage={handleClearImage}
                    onImageUpload={handleImageUpload}
                    disabled={disabled}
                />

                <div className="flex flex-col gap-2">
                    <Label htmlFor={`eyebrow-${slice.id}`}>Eyebrow</Label>
                    <Input
                        type="text"
                        id={`eyebrow-${slice.id}`}
                        name="eyebrow"
                        value={slice.eyebrow ?? ''}
                        placeholder="Enter eyebrow"
                        onChange={handleChange}
                        disabled={disabled}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <div className="flex gap-1 justify-between">
                        <Label htmlFor={`name-${slice.id}`}>Name</Label>
                        <SlugDisplay name={slice.name} sliceId={slice.id} onChange={onChange} />
                    </div>
                    <Input
                        type="text"
                        id={`name-${slice.id}`}
                        name="name"
                        value={slice.name ?? ''}
                        placeholder="Enter name"
                        onChange={handleChange}
                        required
                        disabled={disabled}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <Label htmlFor={`description-${slice.id}`}>Description</Label>
                    <Textarea
                        id={`description-${slice.id}`}
                        name="description"
                        value={slice.description ?? ''}
                        placeholder="Enter description"
                        onChange={handleChange}
                        rows="3"
                        disabled={disabled}
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor={`linkText-${slice.id}`}>Link Text</Label>
                        <Input
                            type="text"
                            id={`linkText-${slice.id}`}
                            name="linkText"
                            value={slice.linkText ?? ''}
                            placeholder="Enter link text"
                            onChange={handleChange}
                            disabled={disabled}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor={`linkUrl-${slice.id}`}>Link URL</Label>
                        <Input
                            type="text"
                            id={`linkUrl-${slice.id}`}
                            name="linkUrl"
                            value={slice.linkUrl ?? ''}
                            placeholder="Enter link URL"
                            onChange={handleChange}
                            disabled={disabled}
                            pattern={CONTENT_LINK_URL_PATTERN}
                            title={CONTENT_LINK_URL_TITLE}
                        />
                    </div>
                </div>
            </div>

            <Separator />

            <Tabs defaultValue={slice.dynamicAisleType ? 'dynamic-aisle' : 'custom-items'}>
                <TabsList className="w-full">
                    <TabsTrigger
                        disabled={disabled}
                        onClick={() =>
                            onChange({
                                dynamicAisleType: '',
                                ...(slice.dynamicAisleType ? { enablePersonalizedOrdering: true } : {}),
                            })
                        }
                        value="custom-items"
                    >
                        Custom Items
                    </TabsTrigger>
                    <TabsTrigger
                        disabled={disabled}
                        onClick={() =>
                            onChange({ dynamicAisleType: 'EASY_REORDER', enablePersonalizedOrdering: false })
                        }
                        value="dynamic-aisle"
                    >
                        Dynamic Aisle
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="custom-items">
                    <div className="flex items-center gap-2 mb-4">
                        <Checkbox
                            id={`maintainItemRank-${slice.id}`}
                            checked={slice.enablePersonalizedOrdering === false}
                            onCheckedChange={(checked) => onChange({ enablePersonalizedOrdering: !checked })}
                            disabled={disabled}
                        />
                        <Label className="font-normal cursor-pointer" htmlFor={`maintainItemRank-${slice.id}`}>
                            Maintain item rank
                        </Label>
                    </div>
                    <ProductTable
                        carouselID={slice.carouselID}
                        dialogTitle={slice.name ?? DISPLAY_NAME}
                        loading={loading}
                        onAddRow={handleAddRow}
                        onMoveRowDown={moveRowDown}
                        onMoveRowUp={moveRowUp}
                        onMoveRowToIndex={handleMoveRowToIndex}
                        onRemoveAllRows={handleRemoveAllRows}
                        onRemoveRow={handleRemoveRow}
                        onRemoveSelectedRows={handleRemoveSelectedRows}
                        onUploadProductCSV={handleUploadProductCSV}
                        options={options}
                        rows={enrichedRows}
                    />
                </TabsContent>
                <TabsContent value="dynamic-aisle">
                    <Select
                        className="w-full"
                        value={slice.dynamicAisleType}
                        onValueChange={(dynamicAisleType) => onChange({ dynamicAisleType })}
                    >
                        <SelectTrigger className="w-full" disabled={disabled}>
                            <div className="flex items-center gap-4 ">
                                <span className="font-medium text-foreground">Type</span>
                                <SelectValue placeholder="None" />
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            {DYNAMIC_AISLE_TYPES.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                    {type.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </TabsContent>
            </Tabs>
        </div>
    );
}

const validateSlice = (slice, sliceIndex) => {
    const errors = [];

    if (slice.linkText && !slice.linkUrl) {
        errors.push(`${DISPLAY_NAME} (position ${sliceIndex}) requires a link URL if link text is provided.`);
    }

    if (!slice.linkText && slice.linkUrl) {
        errors.push(`${DISPLAY_NAME} (position ${sliceIndex}) requires link text if a link URL is provided.`);
    }

    if (!slice.dynamicAisleType && slice.items.length < MIN_PRODUCT_ITEMS) {
        errors.push(
            `${DISPLAY_NAME} (position ${sliceIndex}) requires at least ${MIN_PRODUCT_ITEMS} sellable item or bundle.`,
        );
    }

    if (!slice.imageUrl) {
        errors.push(`${DISPLAY_NAME} (position ${sliceIndex}) has a missing image.`);
    }

    return errors;
};

export default Object.assign(ProductCarousel3, {
    category: 'carousel',
    defaultSlice: DEFAULT_SLICE,
    description:
        'A carousel consisting of clickable banners containing a spotlight image, name, description, and link. On mobile, product cards are displayed in three rows.',
    displayName: DISPLAY_NAME,
    productListKey: 'items',
    thumbnail: 'https://img-cdn.misfitsmarket.com/test/lettuce/Minimap-Thumbnail__Product-Carousel-3__Desktop',
    displayType: 'PRODUCT_CAROUSEL_3',
    validateSlice,
});
